import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { meQueryKey } from '../api/me';
import type { Appointment, CreatedAppointment, ServiceRate, SlotDay } from '../api/types';

/**
 * Запросы клиентского пути записи. Сетка слотов не вычисляется здесь ни в каком
 * виде: сервер учитывает буфер между приёмами, занятость ребёнка у других
 * специалистов, дорогу, горизонт записи и паузу. Фронт рисует то, что дали.
 */

export function useServices(specialistId: number | null) {
  return useQuery({
    queryKey: ['services', specialistId],
    queryFn: () => api.get<ServiceRate[]>('/api/services', { specialistId: specialistId! }),
    enabled: specialistId !== null,
  });
}

export function useSlots(params: { specialistId: number | null; serviceId: number | null; childId: number | null; from?: string; to?: string }) {
  const { specialistId, serviceId, childId, from, to } = params;
  return useQuery({
    queryKey: ['slots', specialistId, serviceId, childId, from, to],
    queryFn: () => api.get<SlotDay[]>('/api/slots', {
      specialistId: specialistId!, serviceId: serviceId!, childId: childId ?? undefined, from, to,
    }),
    enabled: specialistId !== null && serviceId !== null,
    // Слоты устаревают быстро: чужая запись освобождает или занимает сетку.
    staleTime: 15_000,
  });
}

export function useHistory() {
  return useQuery({
    queryKey: ['appointments', 'history'],
    queryFn: () => api.get<Appointment[]>('/api/appointments', { scope: 'history' }),
  });
}

export function useUpcoming() {
  return useQuery({
    queryKey: ['appointments', 'upcoming'],
    queryFn: () => api.get<Appointment[]>('/api/appointments', { scope: 'upcoming' }),
  });
}

/** Инвалидируем и `/api/me`: в нём лежит `upcoming`, с которого живёт главная. */
function useRefreshBookings() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: meQueryKey });
    void qc.invalidateQueries({ queryKey: ['appointments'] });
    void qc.invalidateQueries({ queryKey: ['slots'] });
  };
}

export function useCreateAppointment() {
  const refresh = useRefreshBookings();
  return useMutation({
    mutationFn: (body: { specialistId: number; serviceId: number; childId: number; startsAt: number; intake?: Record<string, unknown> }) =>
      api.post<CreatedAppointment>('/api/appointments', body),
    onSuccess: refresh,
  });
}

/**
 * Родитель подтвердил запись: бронь перестаёт быть временной, анкета уходит
 * вместе с подтверждением. Оплата — на месте, её отметит сотрудник.
 */
export function useConfirmAppointment() {
  const refresh = useRefreshBookings();
  return useMutation({
    mutationFn: (v: { id: number; intake?: Record<string, unknown> }) =>
      api.post<{ appointment: Appointment }>(`/api/appointments/${v.id}/confirm`, { intake: v.intake }),
    onSuccess: refresh,
  });
}

export function useCancelAppointment() {
  const refresh = useRefreshBookings();
  return useMutation({
    mutationFn: (id: number) => api.post<{ appointment: Appointment; refunded: boolean }>(`/api/appointments/${id}/cancel`),
    onSuccess: refresh,
  });
}

export function useRescheduleAppointment() {
  const refresh = useRefreshBookings();
  return useMutation({
    mutationFn: (v: { id: number; startsAt: number }) =>
      api.post<Appointment>(`/api/appointments/${v.id}/reschedule`, { startsAt: v.startsAt }),
    onSuccess: refresh,
  });
}

export function useSaveContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { firstName?: string; phone: string; source: 'form' | 'telegram_contact'; consent: boolean }) =>
      api.post<unknown>('/api/me/contact', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: meQueryKey }),
  });
}
